import { supabase } from '../lib/supabase'

export interface ActivateTrialParams {
  planType: 'premium' | 'escolar'
  deviceFingerprint?: string
  ipAddress?: string
}

export interface ActivateTrialResponse {
  success: boolean
  subscription?: any
  trialEnd?: string
  message?: string
  error?: string
  reason?: string
  cooldownEnds?: string
}

/**
 * Lê o corpo JSON de uma resposta não-2xx.
 *
 * `functions.invoke` lança FunctionsHttpError assim que `response.ok` é falso,
 * sem consumir o corpo, e devolve `{ data: null, error }`. Sem isto o motivo
 * ("Este email já foi usado em um trial anterior") se perde e o usuário vê a
 * mensagem genérica do SDK.
 */
const readErrorBody = async (error: unknown): Promise<Partial<ActivateTrialResponse> | null> => {
  const context = (error as { context?: unknown })?.context
  if (!context || typeof (context as Response).json !== 'function') return null

  try {
    return await (context as Response).json()
  } catch {
    return null
  }
}

/**
 * Activates a trial subscription directly without requiring credit card
 * This is called when user is logged in and clicks "Start Trial"
 */
export const activateTrial = async (
  params: ActivateTrialParams
): Promise<ActivateTrialResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke('activate-trial', {
      body: params
    })

    if (error) {
      const body = await readErrorBody(error)
      return {
        success: false,
        error: body?.error || (error as Error).message || 'Erro ao ativar trial. Tente novamente.',
        reason: body?.reason,
        cooldownEnds: body?.cooldownEnds
      }
    }

    if (data?.error) {
      return {
        success: false,
        error: data.error,
        reason: data.reason,
        cooldownEnds: data.cooldownEnds
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      error: (error as Error)?.message || 'Erro ao ativar trial. Tente novamente.'
    }
  }
}

/**
 * Auto-trial do primeiro login, com UMA nova tentativa para falha transitória.
 *
 * `reason` presente é recusa de negócio (e-mail já usou trial, abuso):
 * repetir não muda nada. Sem `reason` é rede/5xx, e uma falha dessas deixava a
 * sessão inteira sem trial — o usuário caía no billing até voltar outro dia.
 */
export const activateTrialWithRetry = async (
  params: ActivateTrialParams,
  retryDelayMs = 1_500,
  activate: (p: ActivateTrialParams) => Promise<ActivateTrialResponse> = activateTrial
): Promise<ActivateTrialResponse> => {
  const first = await activate(params)
  if (first.success || first.reason) return first

  await new Promise(resolve => setTimeout(resolve, retryDelayMs))
  return activate(params)
}

/**
 * Generates a device fingerprint for trial abuse prevention
 */
export const generateDeviceFingerprint = (): string => {
  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack
  }

  const fingerprintString = JSON.stringify(fingerprint)
  let hash = 0
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }

  return `fp_${Math.abs(hash).toString(36)}`
}

/**
 * Gets plan type from plan name
 * @param planName Plan name (e.g., "Premium", "Escolar", "Premium Anual")
 * @returns Plan type ('premium' or 'escolar')
 */
export const getPlanTypeFromName = (planName: string): 'premium' | 'escolar' => {
  const normalizedName = planName.toLowerCase()
  if (normalizedName.includes('escolar')) {
    return 'escolar'
  }
  return 'premium'
}

/**
 * Formats trial end date for display
 */
export const formatTrialEndDate = (trialEnd: string): string => {
  const date = new Date(trialEnd)
  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
